import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Screen, ScreenTitle, PrimaryButton, ErrorNote } from '../ui/Screen.jsx'
import { useConfigBundle, demographicsFieldsOf } from '../config.js'
import { DemographicField } from '../demographics/DemographicField.jsx'
import { validateDemographics, initialValues, detailVisible, visibleByRule } from '../demographics/validate.js'
import { onboarding } from '../api.js'
import { useMe } from '../auth.js'
import { NavBack } from '../ui/NavBar.jsx'

/**
 * The 14 fields, rendered from config, with config's validation ranges.
 *
 * This is one submit rather than per-field saves: the fields are interdependent
 * (wake and bed time generate the whole beep schedule, two of them gate
 * instruments), so a half-saved demographics row would produce a half-valid
 * schedule. Day 0's ITEMS save per item; this form does not.
 */
export default function Demographics() {
  const navigate = useNavigate()
  const { data: bundle, isLoading } = useConfigBundle()
  /*
   * HIDDEN FIELDS ARE STILL SUBMITTED. They are absent from the form, not from
   * the payload.
   *
   * `demo_notif_channel` has exactly one option since email was retired, so as
   * a question it asks her to choose from a list of one. It is NOT deleted from
   * config - CONTRACT rule 1.25 keeps it because a test compares backend's
   * PUSH_CHANNEL against this option list, and deleting the list would delete
   * the authority that check compares against.
   *
   * So: config still declares it, the server still requires it, the column
   * still fills, and she never sees a question with one answer. The value comes
   * from the field's own single option rather than a literal here, so if config
   * ever offers a real choice again this stops being auto-supplied and the
   * field reappears on its own.
   */
  const allFields = demographicsFieldsOf(bundle)
  const fields = allFields.filter((f) => !f.hidden)
  /*
   * READ-ONLY FIELDS SHOW THE SERVER'S VALUE. Age renders greyed out with the
   * derived number from /auth/me - she should see the fact the study holds,
   * she just cannot edit the wrong copy of it (the dob is where a wrong age
   * gets fixed). The value is folded into `values` so validation sees it as
   * answered; the server overwrites it on PUT regardless, so display and
   * storage cannot disagree for longer than one request.
   */
  const { data: me } = useMe()
  // Hidden single-option fields are auto-supplied (demo_notif_channel).
  // Hidden fields WITHOUT options (demo_age) are simply omitted: the server
  // derives them itself - age from the registration dob - and overwrites
  // whatever a client sends, so sending anything would only imply the client
  // had a say. Omission is the honest payload.
  const hiddenDefaults = Object.fromEntries(
    allFields.filter((f) => f.hidden && (f.options || []).length === 1)
      .map((f) => [f.field_id, f.options[0]]),
  )

  const [values, setValues] = useState(() => initialValues(fields))
  const [seeded, setSeeded] = useState(false)
  const [errors, setErrors] = useState({})
  const [failure, setFailure] = useState(null)
  const [busy, setBusy] = useState(false)
  // The server moved her bed time. Held so she is told before she moves on.
  const [adjustment, setAdjustment] = useState(null)

  // Defaults only exist once config has loaded, which may be after first paint.
  useMemo(() => {
    if (!seeded && fields.length) {
      setValues(initialValues(fields))
      setSeeded(true)
    }
  }, [fields, seeded])

  /*
   * FOLD THE DERIVED AGE INTO `values`, WHICH IS WHAT THE PARAGRAPH ABOVE
   * ALREADY PROMISED AND NOTHING DID.
   *
   * The field rendered `me.age` directly while `values.demo_age` stayed at its
   * initial '', so the box showed 31 and `validateDemographics` — which reads
   * `values` — called it blank and refused: "This one is needed", on a control
   * she cannot type into. submit() returns on the first error, so the funnel
   * was closed at demographics with no way forward.
   *
   * `me` resolves after first paint, so this cannot be part of the initial
   * seed. Guarded on equality so it settles in one pass.
   */
  useEffect(() => {
    if (me?.age === undefined || me?.age === null) return
    setValues((v) => (v.demo_age === me.age ? v : { ...v, demo_age: me.age }))
    // `seeded` is a dependency, not decoration: the seeding below runs when
    // CONFIG arrives and replaces the whole object with initialValues(). If
    // /auth/me resolved first, that wipes the age straight back to '' and this
    // effect never re-fires, because me.age did not change. Order-dependent,
    // so the bug came back only on the runs where config was the slower one.
  }, [me?.age, seeded])

  const set = (fieldId, value) => {
    setValues((v) => {
      const next = { ...v, [fieldId]: value }
      // A detail sub-field that just went off screen must not submit a stale
      // answer: saying "Yes" then "No" would otherwise still send the meds text.
      const parent = fields.find((f) => f.field_id === fieldId && f.detail)
      if (parent && !detailVisible(parent, value)) next[parent.detail.field_id] = ''
      // A field that just went off screen by rule must not submit a stale
      // answer either: "Female" then "Male" would otherwise still send the
      // pregnancy answer. The server discards it too; this keeps the form honest.
      for (const f of fields) {
        if (f.visible_when?.field === fieldId && !visibleByRule(f, next)) next[f.field_id] = ''
      }
      return next
    })
    setErrors((e) => (e[fieldId] ? { ...e, [fieldId]: undefined } : e))
  }

  /*
   * DS-6 §2.2: demographics runs BEFORE install and permission, so the next
   * stop is install. The chosen channel travels with her: there is no GET for
   * demographics and this is the one moment the client knows the answer.
   */
  const goOn = () =>
    navigate('/install', {
      replace: true,
      state: { notifChannel: values.demo_notif_channel ?? null },
    })

  async function submit(e) {
    e.preventDefault()
    setFailure(null)

    const found = validateDemographics(fields, values)
    setErrors(found)
    if (Object.keys(found).length) {
      const first = fields.find((f) => found[f.field_id])
      document.getElementById(first.field_id)?.scrollIntoView({ block: 'center' })
      document.getElementById(first.field_id)?.focus?.()
      return
    }

    setBusy(true)
    try {
      // Hidden fields last: the server still requires them, and a value she
      // was never shown must not be overridable by one she was.
      const result = await onboarding.demographics({ ...hiddenDefaults, ...values })

      /*
       * A6 CLIENT HALF — RENDER THE SERVER'S ADJUSTED bed_time, NEVER COMPUTE IT.
       *
       * The study needs a 12-hour minimum wake→bed span, because the ±20 minute
       * jitter on the middle beeps breaks the source's 90-minute minimum gap on
       * a short day. The server enforces it: it takes what she entered,
       * computes the effective bed_time, stores THAT, and returns it.
       *
       * This client does not know the rule and must not learn it. Two
       * implementations of one rule is where every serious defect in this
       * project has lived, and here the two copies would disagree about when
       * her thirty beeps fire — silently, because both would look right.
       *
       * So this reads a value and renders a sentence. If the server did not
       * adjust anything, or has not shipped its half yet, nothing is shown and
       * she goes straight on: absent is not the same as unchanged, but both are
       * correctly silent.
       */
      const adjusted = adjustedBedTime(result)
      if (adjusted) {
        setAdjustment(adjusted)
        return // she continues from the notice, so she has actually seen it
      }

      goOn()
    } catch (err) {
      setFailure(err)
    } finally {
      setBusy(false)
    }
  }

  if (isLoading && !fields.length) {
    return (
      <Screen>
        <ScreenTitle>About you</ScreenTitle>
        <p className="text-sm text-[var(--ink-700)]">Loading…</p>
      </Screen>
    )
  }

  if (!fields.length) {
    return (
      <Screen>
        <ScreenTitle>About you</ScreenTitle>
        <p className="rounded-[var(--r-surface)] bg-[var(--surface-sunken)] px-4 py-3 text-sm text-[var(--ink-900)]">
          These questions haven&rsquo;t loaded. Please try again shortly.
        </p>
        {import.meta.env.DEV ? (
          <p className="mt-2 font-mono text-xs text-[var(--ink-700)]">
            DEV: no `demographics.fields` in the config bundle — CFG-5 has not landed yet.
          </p>
        ) : null}
      </Screen>
    )
  }

  /*
   * A6 — TELL HER IT MOVED, before she goes on.
   *
   * Her answers are already saved; this is not a confirmation step and it does
   * not ask her to agree. It exists because the times she gave are what she
   * expects the check-ins to follow, and one of them is now different. Finding
   * that out from a beep at an hour she did not choose is how an EMA study
   * loses someone on day one.
   *
   * Stated as a fact with its reason, in her terms — not "12-hour minimum span"
   * or anything about jitter or gaps, which are our constraints and not hers.
   */
  if (adjustment) {
    return (
      <Screen disposition="status" action={<PrimaryButton onClick={goOn}>Continue</PrimaryButton>}>
        <ScreenTitle>We moved your bedtime a little</ScreenTitle>
        <p className="text-base leading-relaxed text-[var(--ink-900)]">
          The check-ins need a longer day than the hours you gave, so we have set your bedtime to{' '}
          <strong className="tabular-nums">{displayTime(adjustment.effective)}</strong>
          {adjustment.original ? (
            <>
              {' '}instead of <span className="tabular-nums">{displayTime(adjustment.original)}</span>
            </>
          ) : null}
          .
        </p>
        <p className="mt-3 text-base leading-relaxed text-[var(--ink-700)]">
          Your check-ins will be spread across that day. Nothing else has changed.
        </p>
      </Screen>
    )
  }

  return (
    <Screen nav={{ left: <NavBack /> }} action={<PrimaryButton type="submit" form="demographics" disabled={busy}>{busy ? 'Saving…' : 'Continue'}</PrimaryButton>}>
      <ScreenTitle subtitle="A few details before we start.">About you</ScreenTitle>

      <form id="demographics" onSubmit={submit} noValidate>
        {fields.filter((field) => visibleByRule(field, values)).map((field) => (
          <DemographicField
            key={field.field_id}
            field={field}
            value={values[field.field_id]}
            values={values}
            onChange={set}
            errors={errors}
          />
        ))}

        <ErrorNote error={failure} />

      </form>
    </Screen>
  )
}

/**
 * The server's bed-time adjustment, if it made one.
 *
 * ONE SHAPE, BACKEND'S, AND NO GUESSES. This briefly accepted several plausible
 * spellings because A6's two halves were built in parallel with no agreed
 * contract. Backend shipped `bed_time`, which was not among them, so the notice
 * never rendered and she was never told her bed time had moved. The guesses are
 * DELETED rather than extended: a list of maybes is a temporary state, and one
 * kept around is just a slower version of the same defect.
 *
 *   "bed_time": {
 *     "entered": "22:00:00", "effective": "21:00:00",
 *     "adjusted": true, "minimum_waking_hours": 12
 *   }
 *
 * THE `adjusted` BOOLEAN DECIDES, never a comparison of the two strings. The
 * server computed that fact while applying the rule and it is the only
 * authority on it. String equality would also be fooled by a formatting
 * difference — "21:00" against "21:00:00" is a change that is not one — which
 * is the client quietly re-deriving a server rule, in miniature, on the exact
 * seam this whole ticket exists to keep single.
 *
 * NOTHING HERE COMPUTES ANYTHING. There is no 12-hour arithmetic in this file
 * and there must never be: the server owns the rule, stores what it computed,
 * and this renders what it is told.
 *
 * @returns {{effective: string, original: string|null, minimumHours: number|null}|null}
 */
export function adjustedBedTime(result) {
  const bedTime = result?.bed_time
  if (!bedTime || typeof bedTime !== 'object') return null
  if (bedTime.adjusted !== true) return null
  if (!bedTime.effective) return null

  return {
    effective: bedTime.effective,
    original: bedTime.entered ?? null,
    minimumHours: bedTime.minimum_waking_hours ?? null,
  }
}

/**
 * "21:00:00" -> "21:00", for display only.
 *
 * The server sends a full time and a participant should not read seconds off a
 * bedtime. This trims presentation and nothing else: it never parses, never
 * shifts a value, and never decides whether an adjustment happened. If the
 * shape is ever anything but HH:MM:SS it is passed through untouched rather
 * than mangled, because a wrong time on this screen is worse than an ugly one.
 */
export function displayTime(value) {
  if (typeof value !== 'string') return value
  const m = /^(\d{2}:\d{2})(:\d{2})?$/.exec(value)
  return m ? m[1] : value
}
